import { GetServerSideProps } from 'next';
import { useAtom, useSetAtom } from 'jotai';
import Image from 'next/image';
import Link from 'next/link';

import PageContainer from '@/components/PageContainer';
import { contentfulAPIURL } from '@/clients/contentful';
import { createApolloClient } from '@/clients/apollo';
import { queryFooter, queryNavbar } from '@/clients/queries';
import { FooterQuery, NavbarQuery } from 'types/generated';
import { getRadioSchedules } from '@/clients/RadioUtils';
import { RadioSchedule } from '@/models/RadioTypes';
import Cart from '@/components/Icons/Cart';
import Plus from '@/components/Icons/Plus';
import Minus from '@/components/Icons/Minus';
import { cartAtom, changeCartItemAtom, cartTotalAtom, cartHashAtom } from '@/store';
import { formatISK, hashCart } from '@/utils/utils';
import Button from 'components/src/button';
import ArrowRight from '@/components/Icons/ArrowRight';
import { useRouter } from 'next/router';
import Close from '@/components/Icons/Close';
import { useEffect, useState } from 'react';
import ImagePlaceholder from '@/components/common/ImagePlaceholder';
import { Log } from '@/logging/datadog';

type Props = {
  navbar: NavbarQuery;
  footer: FooterQuery;
  radioSchedules: Record<string, RadioSchedule[]>;
};

const CartPage = ({ navbar, footer, radioSchedules }: Props) => {
  const [cart] = useAtom(cartAtom);
  const [cartTotal] = useAtom(cartTotalAtom);
  const [availabilityList, setAvailabilityList] = useState<Record<string, number>>({});
  const setCartHash = useSetAtom(cartHashAtom);
  const changeCartItem = useSetAtom(changeCartItemAtom);

  const router = useRouter();

  useEffect(() => {
    const fetchAvailability = async () => {
      const results: Record<string, number> = {};

      for (const key in cart) {
        if (!cart[key]?.product?.id) continue;

        const productId = cart[key].product.id;

        try {
          const res = await fetch(`/api/store/product-available-quantity?id=${productId}`);
          const data = (await res.json()) as { productAvailability: number };
          results[productId] = data.productAvailability;
        } catch (err) {
          Log.error(`Error fetching availability for: ${productId}`, err);
          results[productId] = 0;
        }
      }

      setAvailabilityList(results);
    };

    if (cart && Object.keys(cart).length > 0 && Object.keys(availabilityList).length === 0) {
      fetchAvailability();
    }
  }, [cart, availabilityList]);

  const changeItem = (productId: string, div: number) => {
    const item = cart[productId];
    item.quantity += div;
    changeCartItem(item);
  };

  const addItem = (productId: string) => {
    changeItem(productId, +1);
  };

  const subtractItem = (productId: string) => {
    changeItem(productId, -1);
  };

  const removeItem = (productId: string) => {
    const item = cart[productId];
    item.quantity = 0;
    changeCartItem(item);
  };

  const forward = async () => {
    const hash = await hashCart(cart);
    setCartHash(hash);
    // TODO: Go to customer inputs
    router.push('/vefverslun/karfa/upplysingar');
  };

  return (
    <PageContainer
      title="Sýn | Karfa"
      metaTags={{
        title: 'Sýn Karfa',
        url: 'https://www.syn.is/vefverslun/karfa',
        keywords: 'syn,karfa',
        image: { url: '/android-chrome-192x192.png' },
      }}
      navbar={navbar}
      footer={footer}
      radioSchedules={radioSchedules}
    >
      {Object.keys(cart).length > 0 ? (
        <div className="col-span-full mx-24 mb-24 py-128 md:col-span-12 md:col-start-2 md:mx-0 lg:mb-64">
          <h1 className="mb-24 text-xl font-bold">Karfan mín!</h1>
          <hr className="my-24 text-[#E1E2E3]" />
          <div className="flex w-full flex-col gap-24">
            {Object.keys(cart).map((key) => (
              <div
                key={key}
                className="flex w-full flex-col items-end rounded-16 border-solid border-white-5 bg-white-100 shadow-md"
              >
                <div className="relative right-[28px] top-4 h-0 w-0">
                  <div className="absolute h-16 w-16 text-grey-100">
                    <button className="rounded-full p-4 hover:bg-white-5" onClick={() => removeItem(key)}>
                      <Close width="16" height="16" strokeColor="currentColor" />
                    </button>
                  </div>
                </div>

                <div className="flex w-full flex-col items-center gap-24 px-24 py-16 md:flex-row md:gap-0">
                  <div className="flex w-full items-center">
                    <div className="mr-24 flex h-80 w-80 flex-none items-center justify-center">
                      {cart[key].product?.imageUrl ? (
                        <Image
                          alt={cart[key].product?.imageUrl || ''}
                          src={cart[key].product?.imageUrl || ''}
                          width={80}
                          height={80}
                          className="h-80 w-auto object-contain"
                        />
                      ) : (
                        <div className="h-full w-full rounded-4 bg-white-5 p-24">
                          <ImagePlaceholder bg="bg-white-5" />
                        </div>
                      )}
                    </div>
                    <div className="mr-auto flex flex-col md:w-[300px]">
                      <span className="font-bold">{cart[key].product.name}</span>
                      <span className=" text-black-100">{cart[key].product.variant}</span>
                      <span className=" text-black-100">{cart[key].product.subvariant}</span>
                    </div>
                  </div>
                  <div className="flex w-full justify-between gap-12 md:w-auto md:gap-0">
                    <div className="flex items-center gap-4 text-red-100 md:order-2 md:ml-auto md:w-[160px] md:justify-end">
                      <span className="text-sm">Verð:</span>
                      <span className="text-md font-bold">
                        {formatISK(cart[key].product.price * cart[key].quantity)}
                      </span>
                    </div>
                    <div className="my-auto flex flex-row items-center gap-8 md:order-1">
                      <button
                        className={`flex h-32 w-32 items-center justify-center rounded-4 ${
                          cart[key].quantity > 1 ? 'bg-red-100 text-white-100' : 'bg-white-5 text-grey-100'
                        }`}
                        disabled={cart[key].quantity < 2}
                        onClick={() => subtractItem(key)}
                      >
                        <Minus />
                      </button>
                      <input
                        value={cart[key].quantity}
                        type="text"
                        className="h-40 w-40 rounded-4 border-1 border-solid border-grey-100 text-center"
                      ></input>
                      <button
                        disabled={
                          Object.keys(availabilityList).length === 0 || availabilityList[key] <= cart[key].quantity
                        }
                        className={`flex h-32 w-32 items-center justify-center rounded-4 ${
                          Object.keys(availabilityList).length === 0 || availabilityList[key] <= cart[key].quantity
                            ? 'bg-white-5 text-grey-100'
                            : 'bg-red-100 text-white-100'
                        }`}
                        onClick={() => addItem(key)}
                      >
                        <Plus />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-24 grid w-full grid-cols-4 items-end md:grid-cols-12">
            <div className="col-span-2 grid md:col-start-9 md:justify-end lg:col-span-1 lg:col-start-10">Verð:</div>
            <div className="col-span-2 grid justify-end font-bold">{formatISK(cartTotal)}</div>
            <div className="col-span-full">
              <hr className="my-24 text-[#E1E2E3]" />
            </div>
            <span className="col-span-2 grid font-bold md:col-start-9 md:justify-end lg:col-span-1 lg:col-start-10">
              Samtals:
            </span>
            <span className="col-span-2 grid justify-end text-lg font-bold text-red-100">{formatISK(cartTotal)}</span>
            <div className="col-span-4 mt-24 grid md:col-start-9 lg:col-span-3 lg:col-start-10">
              <Button
                onClick={forward}
                label="Klára kaup"
                bordered
                icon={<ArrowRight />}
                iconPosition="right"
                autoWidth={false}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="col-span-full flex h-full flex-col items-center justify-center py-[240px]">
          <span className="mb-24 text-red-100">
            <Cart width={'64'} height={'64'} />
          </span>
          <h1 className="mb-48 text-2xl">Karfan er tóm</h1>
          <Link className="rounded-full bg-red-100 px-32 py-16 font-bold text-white-100" href="/vefverslun">
            Fara að versla
          </Link>
        </div>
      )}
    </PageContainer>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async () => {
  try {
    const apiUrl = contentfulAPIURL(
      process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
      process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || ''
    );
    const apolloClient = createApolloClient(apiUrl, process.env.NEXT_PUBLIC_CONTENTFUL_CD_TOKEN || '');

    const [footer, navbar, radioSchedules] = await Promise.all([
      queryFooter(apolloClient),
      queryNavbar(apolloClient),
      getRadioSchedules(),
    ]);

    return {
      props: {
        navbar: navbar.data,
        footer: footer.data,
        radioSchedules,
      },
    };
  } catch (err) {
    Log.error((err as Error).message || '', err);

    try {
      const apiUrl = contentfulAPIURL(
        process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
        process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || ''
      );
      const apolloClient = createApolloClient(apiUrl, process.env.NEXT_PUBLIC_CONTENTFUL_CD_TOKEN || '');

      const [footer, navbar, radioSchedules] = await Promise.all([
        queryFooter(apolloClient),
        queryNavbar(apolloClient),
        getRadioSchedules(),
      ]);
      return {
        props: {
          navbar: navbar.data,
          footer: footer.data,
          radioSchedules,
        },
      };
    } catch {
      throw err;
    }
  }
};

export default CartPage;
